import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../redux/store";
import { StoreState } from "../redux/reducers";
import { AuthUserType } from "../redux/actions/authUserActions";
import {
  clearStudentFeeReportAction,
  fetchStudentFeeReportAction,
  StudentFeeReport,
} from "../redux/actions/studentFeeReport";
import { toast } from "react-toastify";
import Main from "../components/custom/Main";
import PageContainer from "../components/custom/PageContainer";
import Card from "../components/custom/Card";
import { LocalFormatDate } from "../config/functions";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import Papa from "papaparse";
import * as Feather from "react-feather";

const BillingReport = () => {
  const location = window.location.pathname;
  const dispatch = useDispatch<AppDispatch>();
  const AuthUser = useSelector<StoreState, AuthUserType | null>(
    (state) => state.authUser,
  );
  const reportList = useSelector<StoreState, StudentFeeReport[]>(
    (state) => state.studentReport,
  );
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  useEffect(() => {
    dispatch(clearStudentFeeReportAction());
  }, []);

  const handleSearch = () => {
    if (!fromDate || !toDate) {
      toast.error("Form Date & To Date");
      return;
    }
    dispatch(clearStudentFeeReportAction());
    dispatch(fetchStudentFeeReportAction({ fromDate, toDate }));
  };

  const handleClear = () => {
    setFromDate("");
    setToDate("");
    dispatch(clearStudentFeeReportAction());
  };

  const downloadBillingPDF = () => {
    if (reportList.length === 0) {
      toast.error("No data to download");
      return;
    }

    const doc = new jsPDF({
      orientation: "landscape",
      unit: "mm",
      format: "a4",
    });

    const title = "Billing Report";
    const pageWidth = doc.internal.pageSize.getWidth();
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(14);
    doc.text(title, pageWidth / 2, 10, { align: "center" });

    doc.setFontSize(10);
    doc.text(
      `From Date: ${LocalFormatDate(fromDate)} | To Date: ${LocalFormatDate(toDate)}`,
      pageWidth / 2,
      18,
      {
        align: "center",
      },
    );

    const headers = [
      "S.No",
      "Bill Date",
      "Bill Number",
      "Admission Number",
      "Name",
      "Payment Mode",
      "Paid Amount",
    ];

    const rows: (string | number)[][] = reportList.map((row, index) => [
      index + 1,
      row.billDate ? LocalFormatDate(row.billDate) : "",
      row.billNo ?? "",
      row.admissionNumber ?? "",
      row.name ?? "",
      row.paymentMode ? row.paymentMode.toUpperCase() : "",
      row.paidAmount ? Number(row.paidAmount).toFixed(2) : "0.00",
    ]);

    autoTable(doc, {
      head: [headers],
      body: rows,
      startY: 25,
      theme: "grid",
      styles: {
        fontSize: 7,
        cellPadding: 2,
        overflow: "linebreak",
        textColor: [0, 0, 0],
        lineColor: 0,
        lineWidth: 0.1,
      },
      headStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
        fontStyle: "bold",
        fontSize: 7,
        lineColor: 0,
        lineWidth: 0.1,
      },
    });

    const totalPaid = reportList.reduce(
      (sum, r) => sum + Number(r.paidAmount || 0),
      0,
    );

    const finalY = (doc as any).lastAutoTable.finalY + 10;
    doc.setFont("helvetica", "bold");
    doc.text(
      `Total Paid Amount : ${totalPaid.toFixed(2)}`,
      pageWidth - 80,
      finalY,
    );

    doc.save("billing_report.pdf");
  };

  const downloadBillingCSV = () => {
    if (reportList.length === 0) {
      toast.error("No data to download");
      return;
    }

    const csvData = reportList.map((row, index) => ({
      "S.No": index + 1,
      "Bill Date": LocalFormatDate(row.billDate),
      "Bill Number": row.billNo,
      "Admission Number": row.admissionNumber,
      Name: row.name,
      "Paid Amount": Number(row.paidAmount).toFixed(2),
    }));

    const csv = Papa.unparse(csvData);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "billing_report.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  const totalPaid = reportList.reduce(
    (sum, r) => sum + Number(r.paidAmount || 0),
    0,
  );
  return (
    <>
      <Main>
        <PageContainer title="Bill Report">
          <Card title="Bill List">
            <div className="row mb-4 align-items-end">
              {/* From Date */}
              <div className="col-sm-6 col-md-3">
                <label>From Date:</label>
                <input
                  type="date"
                  className="form-control"
                  value={fromDate}
                  name="fromDate"
                  onChange={(e) => setFromDate(e.target.value)}
                />
              </div>

              {/* To Date */}
              <div className="col-sm-6 col-md-3">
                <label>To Date:</label>
                <input
                  type="date"
                  className="form-control"
                  value={toDate}
                  name="toDate"
                  onChange={(e) => setToDate(e.target.value)}
                />
              </div>

              {/* Buttons */}
              <div className="col-sm-12 col-md-6 d-flex justify-content-end gap-2">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSearch}
                >
                  Search
                </button>

                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleClear}
                >
                  Clear
                </button>
              </div>
            </div>

            <div className="d-flex justify-content-end mb-3">
              <button
                className="btn btn-success me-2"
                onClick={downloadBillingPDF}
                style={{ fontSize: "14px" }}
              >
                <Feather.Download style={{ width: "20px" }} /> PDF
              </button>

              <button
                className="btn btn-success"
                onClick={downloadBillingCSV}
                style={{ fontSize: "14px" }}
              >
                <Feather.Download style={{ width: "20px" }} /> CSV
              </button>
            </div>

            <div
              className="container-fluid"
              style={{ width: "100%", overflow: "auto" }}
            >
              <table
                className="table table-striped"
                style={{ tableLayout: "fixed", width: "100%" }}
              >
                <thead style={{ backgroundColor: "#10296C", color: "#fff" }}>
                  <tr style={{ fontSize: "15px" }}>
                    <th style={{ width: "75px" }}>S.No</th>
                    <th style={{ width: "150px" }}>Bill Date</th>
                    <th style={{ width: "150px" }}>Bill Number</th>
                    <th style={{ width: "150px" }}>Admission Number</th>
                    <th style={{ width: "150px" }}>Name</th>
                    <th style={{ width: "150px" }}>Payment Mode</th>
                    <th style={{ width: "150px" }}>Paid Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {reportList.length > 0 ? (
                    <>
                      {reportList.map((row, index) => (
                        <tr key={index}>
                          <td>{index + 1}</td>
                          <td>{LocalFormatDate(row.billDate)}</td>
                          <td>{row.billNo}</td>
                          <td>{row.admissionNumber}</td>
                          <td>{row.name}</td>
                          <td style={{ textTransform: "capitalize" }}>
                            {row.paymentMode}
                          </td>
                          <td className="text-end">
                            {Number(row.paidAmount || 0).toFixed(2)}
                          </td>
                        </tr>
                      ))}

                      <tr>
                        <td colSpan={6} className="text-end fw-bold">
                          Total Paid Amount:
                        </td>
                        <td className="text-end fw-bold">
                          {totalPaid.toFixed(2)}
                        </td>
                      </tr>
                    </>
                  ) : (
                    <tr>
                      <td colSpan={6} className="text-center">
                        No Records Found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </PageContainer>
      </Main>
    </>
  );
};

export default BillingReport;
